import React, { useState } from "react";

import { TooltipConfig } from "contexts/toolbars/reducer";

import { TOOLBAR_COLORS, TOOLBAR_Z_INDEX } from "../constants";
import { useToolbarLayer } from "../ToolbarLayerContext";
import { TooltipShortcut } from "./TooltipShortcut";

interface EnhancedTooltipProps {
    config: TooltipConfig;
    children: React.ReactElement;
    /** When true, show the tooltip based on individual button hover instead of area-level hover. */
    showOnHover?: boolean;
}

function getPositionStyle(placement: TooltipConfig["placement"]): React.CSSProperties {
    switch (placement) {
        case "right":
            return { left: "calc(100% + 12px)", top: "50%", transform: "translateY(-50%)" };
        case "top":
            return { bottom: "calc(100% + 12px)", left: "50%", transform: "translateX(-50%)" };
        case "bottom":
            return { top: "calc(100% + 12px)", left: "50%", transform: "translateX(-50%)" };
        case "left":
        default:
            return { right: "calc(100% + 12px)", top: "50%", transform: "translateY(-50%)" };
    }
}

export function EnhancedTooltip({ config, children, showOnHover }: EnhancedTooltipProps) {
    const [isButtonHovered, setIsButtonHovered] = useState(false);
    const { tooltipsVisible } = useToolbarLayer();

    if (!config) {
        return children;
    }

    const isVisible = showOnHover ? isButtonHovered : tooltipsVisible || !!config.alwaysVisible;

    return (
        <div
            style={{ position: "relative" }}
            onMouseEnter={() => setIsButtonHovered(true)}
            onMouseLeave={() => setIsButtonHovered(false)}
        >
            {children}
            <div
                data-testid={`enhanced-tooltip-${config.toolName}`}
                style={{
                    visibility: isVisible ? "visible" : "hidden",
                    position: "absolute",
                    zIndex: TOOLBAR_Z_INDEX.TOOLTIP,
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    height: "28px",
                    padding: "4px 8px",
                    gap: "4px",
                    backgroundColor: isButtonHovered ? TOOLBAR_COLORS.TOOLTIP_HOVERED_BG : TOOLBAR_COLORS.TOOLTIP_BG,
                    border: `1px solid ${TOOLBAR_COLORS.TOOLTIP_HOVERED_BG}`,
                    borderRadius: "8px",
                    boxShadow: TOOLBAR_COLORS.TOOLTIP_SHADOW,
                    fontFamily: TOOLBAR_COLORS.TOOLTIP_FONT_FAMILY,
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                    color: TOOLBAR_COLORS.TOOLTIP_TEXT,
                    boxSizing: "border-box",
                    ...getPositionStyle(config.placement),
                }}
            >
                <TooltipShortcut
                    toolName={config.toolName}
                    shortcutKey={config.shortcutKey}
                    label={config.label}
                    placement={config.placement}
                />
            </div>
        </div>
    );
}
